import { ArtifactActionsFooter } from "@/components/artifacts/ArtifactActionsFooter.tsx";
import { ArtifactErrorBoundary } from "@/components/artifacts/ArtifactErrorBoundary.tsx";
import { ClaimedArtifactView } from "@/components/artifacts/ClaimedArtifactView.tsx";
import {
  ArtifactShareControl,
  canShareArtifact,
} from "@/components/artifacts/ArtifactShareControl.tsx";
import {
  useArtifactReviewPagination,
  useArtifactReviewStaging,
  useArtifactStagedAction,
} from "@/components/artifacts/artifactReviewQueue.ts";
import { isArtifactReadOnly } from "@/components/artifacts/artifactUtils.ts";
import { panelHeaderControlHeightClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { useReleaseBrowsedArtifact } from "@/components/artifacts/browsedArtifact.ts";
import {
  FORM_ARTIFACT_TITLE,
  FormArtifactBody,
} from "@/components/artifacts/formArtifactSurface.tsx";
import { ArtifactView } from "@/components/artifacts/view/ArtifactView.tsx";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import { useArtifactLiveUpdates } from "@/components/artifacts/useArtifactLiveUpdates.ts";
import { useIsSharedThread } from "@/hooks/useIsSharedThread.ts";
import type { HeavyArtifactResponse } from "@/types/artifacts.ts";
import type { ClaimedArtifactResp } from "@/types/sharedArtifact.ts";
import { artifactExpandedTitle } from "@/shared/artifactTitle.ts";
import { Loader } from "@/components/loader/Loader.tsx";
import { useGateValue } from "@statsig/react-bindings";
import { getErrorStatus } from "@/shared/retryPolicy.ts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { CanvasCenteredBody, CanvasModalCard } from "./CanvasModalCard.tsx";
import { RoutineArtifactCanvasCard } from "./RoutineArtifactCanvasCard.tsx";

/**
 * The canvas overlay for an artifact target (e.g. an `emailDraft`). Renders the
 * schema-driven {@link ArtifactView} into the generic {@link CanvasModalCard}
 * shell — the same shell the source overlay uses — with the artifact's action
 * footer (Send / Cancel) supplied via {@link ArtifactActionsFooter}, so the
 * draft stays interactive. The feed chip still opens the in-thread lightbox
 * where there is no canvas (mobile / gate off).
 *
 * The caller resolves the id from the canvas URN; the artifact (with its owning
 * `threadId`, `messageId`, `view`, and `actions`) is fetched by id via
 * `artifactQueries.byId`. The footer/view act on the artifact's own thread
 * (`heavy.threadId`), so a home-screen canvas can drive an artifact from any
 * thread. Rests behind the calendar until it resolves, and shows an error card
 * if the fetch fails.
 */
export const CanvasArtifactModal = ({
  artifactId,
  container,
  showBack,
  isFullscreen,
  onBack,
  onClose,
  onUnloadable,
}: {
  /** The targeted artifact's id, or undefined when no artifact is in the canvas. */
  artifactId: string | undefined;
  /** The stack layer this modal portals into. */
  container: HTMLElement;
  /** Show the leading Back affordance (this layer sits above the stack root). */
  showBack: boolean;
  /** Whether the presented entity is shown fullscreen. */
  isFullscreen: boolean;
  /** Pop this layer. */
  onBack: () => void;
  /** Clear the whole stack. */
  onClose: () => void;
  /**
   * The artifact does not exist for this account and never will — report it so
   * the stack can drop this layer. Only a 404 qualifies; see the effect below.
   */
  onUnloadable?: () => void;
}) => {
  const isSharedThread = useIsSharedThread();
  const userIsAdmin = useGateValue("internal-features");
  // Position in the biscuit's live review queue, so the header can page through
  // it. Stepping moves the biscuit's carousel, which carries this card along with
  // it — the two surfaces are one review, driven from either end.
  const pagination = useArtifactReviewPagination(artifactId);

  // Under review the footer stages rather than fires, and the card says so in
  // place of its buttons. This is the surface BIG-7171 was filed from: clicking
  // Send here used to stage in silence with no way to finish from the card the
  // user was on, which reads as a button that does nothing. Staging now marks
  // the card, steps to the next draft, and — on the last one — commits the whole
  // review, all from the queue owner (see `stageActionAndAdvance`).
  //
  // Outside a review there is nothing to batch with and nothing to page to, so
  // the footer fires and the canvas clears, as it always has.
  const stageAction = useArtifactReviewStaging(artifactId);
  const stagedAction = useArtifactStagedAction(artifactId);
  const handleFired = useMemo(() => (pagination ? () => {} : onClose), [pagination, onClose]);

  // An action that lands settles its card for every surface, the omnibox
  // included: a browsed card would otherwise go on offering the action that was
  // just fired (BIG-7551). On the landed path rather than the optimistic one,
  // because a failed action leaves the artifact still waiting — and taking its
  // card away there would mean reopening it from the feed to retry. Under a
  // review the click stages instead of firing, and browsing takes the review off
  // screen, so the two paths never overlap.
  const releaseBrowsedArtifact = useReleaseBrowsedArtifact();
  const handleActionSettled = useCallback(() => {
    if (artifactId !== undefined) releaseBrowsedArtifact(artifactId);
  }, [artifactId, releaseBrowsedArtifact]);

  // A whole form on the canvas submits for real rather than staging — it is the
  // same endpoint the stepped wizard answers, and an answered form is answered
  // — so this one card does resolve mid-review, and the review steps off it.
  // The last card has nothing to step to and stays, still reachable by the
  // arrows; closing the review out from under the user is theirs to do.
  const handleFormSubmitted = useMemo(() => {
    if (!pagination || pagination.index >= pagination.total - 1) return undefined;
    return pagination.onNext;
  }, [pagination]);

  // Skipping from the last card ends the review — so the card that hosted it
  // goes with it and the thread takes over.
  const cardPagination = useMemo(
    () =>
      pagination && {
        ...pagination,
        onSkip: () => {
          pagination.onSkip();
          onClose();
        },
      },
    [pagination, onClose],
  );

  const { data, error, isError, isLoading } = useQuery(artifactQueries.byId(artifactId));
  const heavy = data?.isHeavy ? (data as HeavyArtifactResponse) : null;

  // A 404 means the artifact is not servable to this account and no amount of
  // waiting changes that — the canvas can be pointed at one by a stale persisted
  // stack, or by an artifact that went hidden after it was presented (BIG-7013).
  // Report it so the stack drops this layer, instead of leaving a dead card the
  // user cannot clear: the stack is persisted, so the card returns on every
  // visit.
  //
  // Deliberately only a 404. A 5xx, a timeout, or an offline client are all
  // transient, and discarding the user's canvas target over a blip would lose
  // real state — those keep the error card, which the retry policy is already
  // built around (`shouldRetryRequest` retries them and not this).
  const isMissing = isError && getErrorStatus(error) === 404;
  useEffect(() => {
    if (isMissing) onUnloadable?.();
  }, [isMissing, onUnloadable]);

  // Refetch when the artifact's own thread reports a change, so an approve fired
  // from another surface (the feed lightbox, the omnibox biscuit) drops this
  // card's now-stale action buttons instead of leaving Approve on screen.
  useArtifactLiveUpdates(artifactId, heavy?.threadId);

  // No artifact targeted (query disabled) → rest the calendar behind.
  if (artifactId === undefined) return null;

  const shellProps = { container, showBack, isFullscreen, onBack, onClose };
  // The pager rides along on every shell this component renders — including the
  // error and not-yet-viewable cards, so a card that fails to load doesn't strand
  // the user mid-queue. An approved routine (`RoutineArtifactCanvasCard`) is past
  // `waitingForUserAction` and so never under review, hence plain `shellProps`.
  const cardProps = { ...shellProps, pagination: cardPagination };

  // The fetch failed (deleted / 404 / network). Render the shell with an error
  // body so the close/back controls stay reachable, rather than silently
  // blanking the canvas. (The ArtifactErrorBoundary below only catches render
  // errors inside ArtifactView, not this fetch failure.)
  if (isError) {
    return (
      <CanvasModalCard title="Artifact" {...cardProps}>
        <CanvasCenteredBody>This item couldn't be loaded.</CanvasCenteredBody>
      </CanvasModalCard>
    );
  }

  // A `userResponse` form is the one canvas artifact that isn't heavy: it has no
  // server-built view to render, so it renders the shared form body instead
  // (BIG-7271). This is where the omnibox form biscuit's preview click lands —
  // the whole form at once, rather than the stepped wizard's one question.
  const formArtifact = data?.artifactType === "userResponse" ? data : undefined;
  if (formArtifact?.form && formArtifact.threadId !== undefined && formArtifact.threadId !== null) {
    return (
      <CanvasModalCard title={FORM_ARTIFACT_TITLE} {...cardProps}>
        <FormArtifactBody
          artifact={formArtifact}
          threadId={formArtifact.threadId}
          // Submitting decides this card, so the review moves on: to the next
          // card if there is one, otherwise the card stays (queue) or closes
          // (lone artifact).
          onSubmitted={handleFormSubmitted ?? handleFired}
        />
      </CanvasModalCard>
    );
  }

  // Someone else's artifact, reached through a share grant. It carries no
  // thread, which the branch below would read as unrenderable, and a shared
  // item from the Library lands here because the canvas claims its path.
  const claimed = heavy?.isOwner === false ? (data as ClaimedArtifactResp) : null;
  if (claimed && heavy?.view) {
    return (
      <CanvasModalCard title={artifactExpandedTitle(claimed)} {...cardProps}>
        <ClaimedArtifactView
          artifact={claimed}
          view={heavy.view}
          userIsAdmin={userIsAdmin}
          onRequestClose={onClose}
        />
      </CanvasModalCard>
    );
  }

  // Every remaining canvas artifact is a heavy one carrying a server-built view
  // (the form above is the sole exception). Its actions/view act on the
  // artifact's own thread — the by-id fetch always stamps it, so a missing
  // threadId means the artifact isn't renderable here.
  const threadId = heavy?.threadId ?? undefined;
  if (!heavy?.view || threadId === undefined) {
    // The root rests behind the calendar only while its query is unresolved.
    // Once settled, every layer keeps a dismissible card so persisted content
    // can never leave the canvas blank.
    if (isLoading && !showBack) return null;
    return (
      <CanvasModalCard title={isLoading ? "Loading…" : "Artifact"} {...cardProps}>
        {isLoading ? (
          <div className="flex flex-1 min-h-0 items-center justify-center p-6">
            <Loader />
          </div>
        ) : (
          <CanvasCenteredBody>This item can't be viewed yet.</CanvasCenteredBody>
        )}
      </CanvasModalCard>
    );
  }

  const isReadOnly = isArtifactReadOnly(heavy, isSharedThread);

  // The frame scrolls itself, so the card hands it the whole column instead.
  const isApplet = heavy.artifactType === "applet";

  // An approved routine is otherwise "final" and so read-only, but a live
  // routine still needs tweaking (schedule / prompt / permissions). Keep it
  // editable behind an explicit Save on this authored surface only — never on a
  // shared (read-only) thread.
  if (heavy.artifactType === "routineArtifact" && heavy.status === "approved" && !isSharedThread) {
    return (
      <RoutineArtifactCanvasCard
        artifact={heavy}
        view={heavy.view}
        threadId={threadId}
        isSharedThread={isSharedThread}
        userIsAdmin={userIsAdmin}
        {...shellProps}
      />
    );
  }

  return (
    <CanvasModalCard
      title={artifactExpandedTitle(heavy)}
      {...cardProps}
      bodyFills={isApplet}
      pageBackground={isApplet}
      // In the header rather than beside the actions: the footer's trailing edge
      // is where the floating debug/feedback launchers sit, and they cover it.
      // An extra rather than an action: it owns a popover off its own trigger,
      // which a menu item has nowhere to put.
      headerExtras={
        canShareArtifact(heavy, { isSharedThread }) && (
          <ArtifactShareControl
            artifactId={heavy.id}
            artifactType={heavy.artifactType}
            title={artifactExpandedTitle(heavy)}
            // Level with the frame's own controls in this row. It carries a
            // label, so it takes their height rather than their square.
            triggerClassName={panelHeaderControlHeightClass}
          />
        )
      }
      footer={
        <ArtifactActionsFooter
          artifact={heavy}
          threadId={threadId}
          onFired={handleFired}
          onSucceeded={handleActionSettled}
          onStage={stageAction}
          stagedAction={stagedAction}
        />
      }
    >
      <ArtifactErrorBoundary
        artifact={heavy}
        threadId={threadId}
        messageId={heavy.messageId}
        userIsAdmin={userIsAdmin}
      >
        <ArtifactView
          view={heavy.view}
          threadId={threadId}
          messageId={heavy.messageId}
          artifactId={heavy.id}
          isReadOnly={isReadOnly}
          isSharedThread={isSharedThread}
          maximized
          fill={isApplet}
          onRequestClose={onClose}
        />
      </ArtifactErrorBoundary>
    </CanvasModalCard>
  );
};
